/** Hulpfuncties voor FocusHero: timer, caption en Paper-styling. */
import { alpha } from '@mui/material/styles';
import {
  getTaskProjects,
  getTaskContexts,
} from '@/features/tasks/utils/data/taskTags';

export const FOCUS_TICK_MS = 1000;
export const FOCUSED_BG_ALPHA = 0.06;
const SECONDS_PER_MINUTE = 60;

const pad2 = (n) => String(n).padStart(2, '0');

/** @param {import('@/types').Task} task @param {Map<string, string>} [projectDisplayMap] */
export const buildFocusCaption = (task, projectDisplayMap = new Map()) => {
  const projectId = getTaskProjects(task)[0];
  const project = projectId
    ? projectDisplayMap.get(projectId) ||
      projectDisplayMap.get(String(projectId).toLowerCase()) ||
      projectId
    : null;
  const context = getTaskContexts(task)[0];
  return [project, context ? `@${context}` : null].filter(Boolean).join(' · ');
};

/** @param {boolean} isFocused @param {number | null | undefined} focusStartTime @param {Date} currentTime */
export const getElapsedSeconds = (isFocused, focusStartTime, currentTime) => {
  const timerRunning = Boolean(isFocused && focusStartTime);
  if (!timerRunning) return { timerRunning, elapsedSeconds: 0 };
  const elapsedSeconds = Math.max(
    0,
    Math.floor((currentTime.getTime() - focusStartTime) / FOCUS_TICK_MS),
  );
  return { timerRunning, elapsedSeconds };
};

/** @param {number} elapsedSeconds */
export const formatElapsedTime = (elapsedSeconds) =>
  `${pad2(Math.floor(elapsedSeconds / SECONDS_PER_MINUTE))}:${pad2(elapsedSeconds % SECONDS_PER_MINUTE)}`;

/** @param {object} params @param {import('@mui/material').Theme} params.theme @param {boolean} params.isFocused @param {number | null | undefined} params.focusStartTime @param {import('@/types').Task} params.task */
export const getFocusHeroPaperSx = ({
  theme,
  isFocused,
  focusStartTime,
  task,
}) => {
  const hasProgressBar = Boolean(focusStartTime && task.duration);
  const secondaryMain = theme.palette.secondary.main;

  return {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    py: '14px',
    px: 2,
    bgcolor: isFocused
      ? alpha(secondaryMain, FOCUSED_BG_ALPHA)
      : 'background.paper',
    overflow: 'visible',
    borderLeft: 4,
    borderLeftColor: isFocused ? 'secondary.main' : 'text.disabled',
    borderBottomLeftRadius: hasProgressBar ? 0 : undefined,
    borderBottomRightRadius: hasProgressBar ? 0 : undefined,
    '@keyframes focusSpin': {
      to: { '--border-angle': '360deg' },
    },
    '&::before': isFocused
      ? {
          content: '""',
          position: 'absolute',
          inset: -2,
          borderRadius: 1,
          padding: '2px',
          pointerEvents: 'none',
          background: `conic-gradient(from var(--border-angle), transparent, ${secondaryMain} 20%, transparent 40%)`,
          WebkitMask:
            'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          animation: 'focusSpin 2.5s linear infinite',
        }
      : undefined,
  };
};
